/**
 * ⭐ 审批工作台（/approvals）。
 *
 * 三个 tab：
 *   全部待审批（默认，与全国态势「审批积压」KPI 同口径）· 我发起的 · 已办结
 * 「全部待审批」内提供「只看待我审批」开关（保留 todoOnly 能力）。
 * 底部：解锁申请列表。
 *
 * 冻结机制在这里是「审批通过 → 冻结」的落点：同意最后一个节点后
 * 任务即进入 FROZEN，抽屉里会出现冻结提示条。
 *
 * ⭐ P6.52：此前默认 tab 是「待我审批」，导致全国态势 KPI「审批积压」
 *    显示的是数据范围内的全部在途审批（如超管看到 1 条），点进来却因
 *    todoOnly 过滤到 0 条而空白。现改为默认「全部待审批」，让 KPI 数字与
 *    列表条数一致，并显式区分「全部待审批 / 待我审批」两个数量。
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Panel from '../../components/Panel';
import Badge from '../../components/Badge';
import TodoList from './TodoList';
import ApproveDialog from './ApproveDialog';
import UnlockRequestList from './UnlockRequestList';
import { useApprovalBuckets, useStuckTasks } from './useApprovals';
import { useUiStore } from '../../stores/ui';
import type { ApprovalAction } from './ApproveDialog';
import type { ApprovalInstanceInfo } from '@atlaspm/shared';
import './approval.css';

const TAB_KEYS = ['pending', 'mine', 'done'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'pending', label: '全部待审批' },
  { key: 'mine', label: '我发起的' },
  { key: 'done', label: '已办结' },
];

function isTabKey(v: string | null): v is TabKey {
  return v !== null && (TAB_KEYS as readonly string[]).includes(v);
}

export default function ApprovalWorkbench() {
  const buckets = useApprovalBuckets();
  const stuck = useStuckTasks();
  /** ⭐ P8.20：复用全局任务详情抽屉（与星图 / 地图同一条链路） */
  const openTask = useUiStore((s) => s.openTask);
  const [searchParams] = useSearchParams();
  // ⭐ P6.52：支持下钻链接携带 tab（KpiBar 的「审批积压」→ /approvals?tab=pending）
  const [tab, setTab] = useState<TabKey>(() => {
    const t = searchParams.get('tab');
    return isTabKey(t) ? t : 'pending';
  });
  const [todoOnlyView, setTodoOnlyView] = useState(false);
  const [dialogItem, setDialogItem] = useState<ApprovalInstanceInfo | null>(null);
  const [dialogAction, setDialogAction] = useState<ApprovalAction>('approve');

  const openDialog = (item: ApprovalInstanceInfo, action: ApprovalAction) => {
    setDialogItem(item);
    setDialogAction(action);
  };

  const current =
    tab === 'pending'
      ? todoOnlyView
        ? buckets.todo
        : buckets.pending
      : tab === 'mine'
        ? buckets.mine
        : buckets.done;

  const countOf = (key: TabKey) =>
    key === 'pending'
      ? buckets.counts.pending
      : key === 'mine'
        ? buckets.counts.mine
        : buckets.counts.done;

  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">审批工作台</h1>
        <div className="page__sub">待办审批 · 同意 / 退回 · 解锁申请</div>
      </div>

      {(stuck.data?.total ?? 0) > 0 && (
        <div className="cf-error" role="alert">
          ⚠ 有 {stuck.data?.total} 个任务卡在「审批中」但已无进行中的审批实例
          （SLA 超时）。请在「已办结」中找到对应实例，点「终止超时审批」后重新提交。
        </div>
      )}

      <div className="approval-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={['approval-tab', tab === t.key ? 'is-on' : ''].join(' ')}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <Badge tone={tab === t.key ? 'brand' : 'idle'}>{countOf(t.key)}</Badge>
          </button>
        ))}
      </div>

      <Panel
        title={TABS.find((t) => t.key === tab)?.label}
        extra={
          tab === 'pending' ? (
            <label className="approval-todo-toggle">
              <input
                type="checkbox"
                checked={todoOnlyView}
                onChange={(e) => setTodoOnlyView(e.target.checked)}
              />
              只看待我审批（{buckets.counts.todo}）
            </label>
          ) : (
            <Badge tone="run">{current.length} 条</Badge>
          )
        }
      >
        {tab === 'pending' ? (
          <>
            <div className="approval-pending-summary">
              全部待审批 <b>{buckets.counts.pending}</b> 条
              <span className="approval-pending-sep">·</span>
              其中待我审批 <b>{buckets.counts.todo}</b> 条
            </div>
            <TodoList
              items={current}
              isLoading={buckets.isLoading}
              isError={buckets.isError}
              error={buckets.error}
              emptyTitle={todoOnlyView ? '暂无待我审批' : '暂无待审批'}
              emptyHint={
                todoOnlyView
                  ? '当前没有轮到你审批的任务'
                  : '当前数据范围内没有进行中的审批'
              }
              actionabled
              onOpen={(item) => openTask(item.taskId)}
              onApprove={(item) => openDialog(item, 'approve')}
              onReject={(item) => openDialog(item, 'reject')}
            />
          </>
        ) : tab === 'mine' ? (
          <TodoList
            items={buckets.mine}
            isLoading={buckets.isLoading}
            isError={buckets.isError}
            error={buckets.error}
            emptyTitle="你还没有发起过审批"
            emptyHint="在任务详情抽屉中提交审批后，会显示在这里"
            onOpen={(item) => openTask(item.taskId)}
            onTerminate={(item) => openDialog(item, 'terminate')}
          />
        ) : (
          <TodoList
            items={buckets.done}
            isLoading={buckets.isLoading}
            isError={buckets.isError}
            error={buckets.error}
            emptyTitle="暂无已办结"
            emptyHint="审批通过 / 退回 / 超时的实例会显示在这里"
            onOpen={(item) => openTask(item.taskId)}
            onTerminate={(item) => openDialog(item, 'terminate')}
          />
        )}
      </Panel>

      <Panel title="解锁申请" extra={<Badge tone="frozen">🔓</Badge>}>
        <UnlockRequestList />
      </Panel>

      <ApproveDialog
        item={dialogItem}
        action={dialogAction}
        open={dialogItem !== null}
        onClose={() => setDialogItem(null)}
      />
    </div>
  );
}
